import {
  StatisticsCounters,
  useDocumentSourcesTableStore,
} from '@terminal/views/alfred/document-search/document-sources-view/document-sources-table/use-document-sources-table-store/useDocumentSourcesTableStore';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type SourcesFilterMessage = {
  totalDocuments?: number;
  totalCompanies?: number;
  totalCitations?: number;
  isGroupedByDocument?: boolean;
  statistics?: { company?: StatisticsCounters; country?: StatisticsCounters };
};

export const useSourcesFilterMessages = ({
  totalDocuments = 0,
  totalCompanies = 0,
  totalCitations = 0,
  statistics,
  isGroupedByDocument,
}: SourcesFilterMessage) => {
  const { t } = useTranslation(['chat', 'common']);
  const { selectedSourceFilter, hasVisibleSourceFilter } =
    useDocumentSourcesTableStore(state => state);
  const activeDocumentFilterOptions = useDocumentSourcesTableStore(
    state => state.filterGroups.documentFilters.activeFilterOptions
  );

  const resolvedTotalDocumentCount = t('chat:documentCount', {
    documentCount: totalDocuments,
    count: totalDocuments,
  });

  const documentsFromCompanies = statistics?.company?.documents ?? 0;
  const resolvedCompanyDocumentCount = t('chat:documentCount', {
    count: documentsFromCompanies,
  });
  const documentsFromCountries = statistics?.country?.documents ?? 0;
  const resolvedCountryDocumentCount = t('chat:documentCount', {
    count: documentsFromCountries,
  });
  const resolvedSubTypeCount = t('chat:subType', {
    count: totalCompanies,
    context: selectedSourceFilter,
  });

  const resolvedCompanySubTypeCount = t('chat:subType', {
    count: statistics?.company?.companies ?? 0,
    context: 'company',
  });

  const resolvedCountrySubTypeCount = t('chat:subType', {
    count: statistics?.country?.countries ?? 0,
    context: 'country',
  });

  const resolvedAllSourcesMessage = useMemo(() => {
    const hasCompanyDocuments = documentsFromCompanies > 0;
    const hasCountryDocuments = documentsFromCountries > 0;

    const companyMessage = t('chat:acrossSourcesList', {
      documentString: resolvedCompanyDocumentCount,
      subTypeString: resolvedCompanySubTypeCount,
    });

    const countryMessage = t('chat:acrossSourcesList', {
      documentString: resolvedCountryDocumentCount,
      subTypeString: resolvedCountrySubTypeCount,
    });

    if (hasCompanyDocuments && hasCountryDocuments) {
      return `${companyMessage} ${t('chat:and')} ${countryMessage}`;
    }

    if (hasCompanyDocuments) {
      return companyMessage;
    }

    if (hasCountryDocuments) {
      return countryMessage;
    }

    return companyMessage;
  }, [
    resolvedCompanyDocumentCount,
    resolvedCompanySubTypeCount,
    resolvedCountryDocumentCount,
    resolvedCountrySubTypeCount,
    documentsFromCompanies,
    documentsFromCountries,
  ]);

  const acrossSourcesListMessage = useMemo(() => {
    return t('chat:acrossSourcesList', {
      documentString: resolvedTotalDocumentCount,
      subTypeString: resolvedSubTypeCount,
    });
  }, [resolvedTotalDocumentCount, resolvedSubTypeCount]);

  const resolvedPassages = t('chat:passageCount', {
    passageCount: totalCitations,
    count: totalCitations,
  });

  const sourceSelectedBaseMessage =
    (!statistics?.company || !statistics?.country) &&
    (!hasVisibleSourceFilter || activeDocumentFilterOptions.length)
      ? acrossSourcesListMessage
      : resolvedAllSourcesMessage;

  const sourcesListSubheader = useMemo(() => {
    if (isGroupedByDocument) {
      return resolvedPassages;
    }

    return sourceSelectedBaseMessage;
  }, [isGroupedByDocument, resolvedPassages, sourceSelectedBaseMessage]);

  const sourcesListHeader = useMemo(() => {
    if (!isGroupedByDocument) {
      return resolvedPassages;
    }

    return sourceSelectedBaseMessage;
  }, [sourceSelectedBaseMessage, isGroupedByDocument, resolvedPassages]);

  return {
    sourceSelectedBaseMessage,
    sourcesListSubheader,
    sourcesListHeader,
  };
};
